import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createWorkshopInstance, getWorkshopDetails, getWorkshopInstances } from "../../api/workshops";
import { CreateWorkshopInstanceDto } from "../../types/workshopTypes";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { AxiosError } from "axios";
import { authReducerActionType } from "../../types/globalTypes";
import { useContext } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";

/**
 * Hook to get a specific workshop by ID and its instances
 * @param workshopId The ID of the workshop to get
 * @returns The workshop details, workshop instances, loading states, and error states
 */
export const useWorkshopDetails = (workshopId: number) => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const {
    data: workshopData,
    isLoading: isWorkshopDetailsLoading,
    isError: isWorkshopDetailsError,
  } = useQuery({
    queryKey: ["workshopDetails", workshopId],
    queryFn: () => getWorkshopDetails(workshopId),
    enabled: !!workshopId,
  });

  const {
    data: workshopInstancesData,
    isLoading: isWorkshopInstancesLoading,
    isError: isWorkshopInstancesError,
  } = useQuery({
    queryKey: ["workshopInstances", workshopId],
    queryFn: () => getWorkshopInstances(workshopId),
    enabled: !!workshopId,
  });

  const workshopDetails = workshopData?.data;
  const workshopInstances = workshopInstancesData?.data;

  // ----------------------------------------------------------------------------------
  // ############
  // CREATE QUERIES
  // ############

  // createWorkshopInstance mutation
  const { mutateAsync: mutateCreateWorkshopInstance } = useMutation({
    mutationFn: ({
      workshopId,
      workshopInstanceData,
    }: {
      workshopId: number;
      workshopInstanceData: CreateWorkshopInstanceDto;
    }) => createWorkshopInstance(workshopId, workshopInstanceData),
    onError: (err: AxiosError) => {
      showErrorMessage("Workshop-Termin erstellen fehlgeschlagen");
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async (_, { workshopId }) => {
      await queryClient.invalidateQueries({ queryKey: ["workshopInstances", workshopId] });
      showSuccessMessage("Workshop-Termin erfolgreich erstellt");
    },
  });

  /**
   * Function to create a new workshop instance
   * @param workshopId The ID of the workshop
   * @param workshopInstanceData The workshop instance data to create
   */
  const createWorkshopInstanceHandler = async (workshopId: number, workshopInstanceData: CreateWorkshopInstanceDto) => {
    return await mutateCreateWorkshopInstance({ workshopId, workshopInstanceData });
  };

  return {
    workshopDetails,
    workshopInstances,
    isWorkshopDetailsLoading,
    isWorkshopDetailsError,
    isWorkshopInstancesLoading,
    isWorkshopInstancesError,
    isWorkshopDetailsAndInstancesLoading: isWorkshopDetailsLoading || isWorkshopInstancesLoading,
    isWorkshopDetailsAndInstancesError: isWorkshopDetailsError || isWorkshopInstancesError,
    // Functions
    createWorkshopInstance: createWorkshopInstanceHandler,
  };
};

export default useWorkshopDetails;
